import React, { useState, useCallback } from "react";
import "./listaAnexos.css";

const MIME_POR_EXT = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp' };
const MIME_DOWNLOAD = { ...MIME_POR_EXT, pdf: 'application/pdf', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' };

function ehImagem(nomeArquivo) {
    const match = (nomeArquivo || '').match(/\.([a-z0-9]+)$/i);
    return match && MIME_POR_EXT[match[1].toLowerCase()];
}

function normalizarConteudoArquivo(val) {
    if (val == null) return '';
    if (typeof val === 'string') return val.trim();
    if (val && val.type === 'Buffer' && Array.isArray(val.data)) {
        const bytes = new Uint8Array(val.data);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        return btoa(binary);
    }
    return '';
}

function payloadBase64(str) {
    if (!str || typeof str !== 'string') return '';
    const s = str.trim().replace(/\s/g, '');
    const idx = s.indexOf('base64,');
    return idx >= 0 ? s.substring(idx + 7) : s;
}

function dataUrlAnexo(nomeArquivo, base64) {
    if (!base64 || typeof base64 !== 'string') return null;
    const s = base64.trim();
    if (s.startsWith('data:')) return s;
    const match = (nomeArquivo || '').match(/\.([a-z0-9]+)$/i);
    const mime = (match && MIME_POR_EXT[match[1].toLowerCase()]) || 'image/jpeg';
    const payload = payloadBase64(base64);
    if (!payload) return null;
    return 'data:' + mime + ';base64,' + payload;
}

function ListaAnexosSolicitante(props) {
    const [previewPopup, setPreviewPopup] = useState({ open: false, url: null, nome: '', ehImagem: false });

    const abrirNoPopup = useCallback((url, nome, ehImagem) => {
        setPreviewPopup({ open: true, url, nome: nome || 'Anexo', ehImagem });
    }, []);

    const fecharPopup = useCallback(() => {
        setPreviewPopup(prev => {
            if (prev.url && prev.url.startsWith('blob:')) {
                try { URL.revokeObjectURL(prev.url); } catch (_) {}
            }
            return { open: false, url: null, nome: '', ehImagem: false };
        });
    }, []);

    const getMimeFromNome = (nome) => {
        const match = (nome || '').match(/\.([a-z0-9]+)$/i);
        const ext = match ? match[1].toLowerCase() : '';
        return MIME_DOWNLOAD[ext] || 'application/octet-stream';
    };

    const base64ToBlob = (base64Data, mimeFallback) => {
        const payload = payloadBase64(base64Data);
        if (!payload) return null;
        let mime = 'application/octet-stream';
        if (typeof base64Data === 'string' && base64Data.indexOf(';') >= 0) {
            const m = base64Data.split(';')[0].split(':')[1];
            if (m) mime = m;
        }
        if (mime === 'application/octet-stream' && mimeFallback) mime = mimeFallback;
        try {
            const byteCharacters = atob(payload);
            const byteNumbers = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            return new Blob([byteNumbers], { type: mime });
        } catch (_) {
            return null;
        }
    };

    const downloadFile = (nome, arquivo) => {
        try {
            const blob = base64ToBlob(arquivo, getMimeFromNome(nome));
            if (!blob) return;
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = nome || 'anexo';
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro ao fazer download:', error);
        }
    };

    const abrirArquivoNoPopup = (nome, arquivo) => {
        try {
            const blob = base64ToBlob(arquivo, getMimeFromNome(nome));
            if (!blob) return;
            const url = URL.createObjectURL(blob);
            abrirNoPopup(url, nome, false);
        } catch (error) {
            console.error('Erro ao abrir anexo:', error);
        }
    };

    const abrirAnexo = (nome, b64, srcImg, urlDownload) => {
        if (srcImg) abrirNoPopup(srcImg, nome, true);
        else if (b64) abrirArquivoNoPopup(nome, b64);
        else if (urlDownload) window.open(urlDownload, '_blank');
    };

    if (props.compact) {
        return (
            <>
                {previewPopup.open && (
                    <div className="anexos-preview-overlay" onClick={fecharPopup} role="dialog" aria-modal="true" aria-label="Visualizar anexo">
                        <div className="anexos-preview-box" onClick={(e) => e.stopPropagation()}>
                            <div className="anexos-preview-header">
                                <span className="anexos-preview-titulo">{previewPopup.nome}</span>
                                <button type="button" className="anexos-preview-fechar" onClick={fecharPopup} aria-label="Fechar">&times;</button>
                            </div>
                            <div className="anexos-preview-body">
                                {previewPopup.ehImagem ? (
                                    <img src={previewPopup.url} alt={previewPopup.nome} className="anexos-preview-img" />
                                ) : (
                                    <iframe src={previewPopup.url} title={previewPopup.nome} className="anexos-preview-iframe" />
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <div className="anexos-lista-compact">
                    {(props.arrayAnexos || []).map((anexo, idx) => {
                        const nome = anexo.nomArq || anexo.NOMARQ || '';
                        const b64Raw = anexo.arquivo ?? anexo.ARQUIVO ?? anexo.Arquivo64 ?? anexo.base64;
                        const b64 = normalizarConteudoArquivo(b64Raw);
                        const mostraImg = ehImagem(nome) && b64;
                        const urlDownload = props.baseUrlDownload && (anexo.satNum || props.satNum) && (anexo.seqAne != null) ? `${props.baseUrlDownload}?satNum=${anexo.satNum ?? props.satNum}&seqAne=${anexo.seqAne}` : null;
                        const srcImg = mostraImg ? dataUrlAnexo(nome, b64) : null;
                        return (
                            <button key={(anexo.seqAne || idx) + '-' + idx} type="button" className="anexos-item-compact" onClick={() => abrirAnexo(nome, b64, srcImg, urlDownload)} title="Clique para exibir">
                                {srcImg ? (
                                    <span className="anexos-item-compact-thumb"><img src={srcImg} alt="" /></span>
                                ) : (
                                    <span className="anexos-item-compact-icon"><i className="fas fa-file-alt" aria-hidden="true" /></span>
                                )}
                                <span className="anexos-item-compact-nome">{nome}</span>
                            </button>
                        );
                    })}
                </div>
            </>
        );
    }

    return (
        <table className="table table-modern align-middle lista-anexos">
            <thead>
                <tr>
                    <th scope="col" className="w-50">Nome Arquivo</th>
                    <th scope="col" className="w-25">Sequência</th>
                    <th scope="col" className="w-25">Preview</th>
                    <th scope="col" className="w-25"></th>
                </tr>
            </thead>
            <tbody>
                {(props.arrayAnexos || []).map((anexo, idx) => {
                    const nome = anexo.nomArq || anexo.NOMARQ || '';
                    const b64Raw = anexo.arquivo ?? anexo.ARQUIVO ?? anexo.Arquivo64 ?? anexo.base64;
                    const b64 = normalizarConteudoArquivo(b64Raw);
                    const urlDownload = props.baseUrlDownload && (anexo.satNum || props.satNum) && (anexo.seqAne != null) ? `${props.baseUrlDownload}?satNum=${anexo.satNum ?? props.satNum}&seqAne=${anexo.seqAne}` : null;
                    const mostraImg = ehImagem(nome) && b64;
                    const srcImg = mostraImg ? dataUrlAnexo(nome, b64) : null;
                    return (
                        <tr key={(anexo.seqAne || idx) + '-' + idx}>
                            <td className="align-middle"><span className="nome-arquivo">{nome}</span></td>
                            <td className="align-middle">{anexo.seqAne}</td>
                            <td className="align-middle">
                                {srcImg ? (
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <a href={srcImg} target="_blank" rel="noopener noreferrer" className="preview-imagem" title="Clique para ampliar">
                                            <img src={srcImg} alt={nome} title={nome} onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                                        </a>
                                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => abrirArquivoNoPopup(nome, b64)}>Abrir</button>
                                    </div>
                                ) : b64 ? (
                                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => abrirArquivoNoPopup(nome, b64)}>Abrir arquivo</button>
                                ) : urlDownload ? (
                                    <a href={urlDownload} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">Abrir no servidor</a>
                                ) : (
                                    <span className="text-muted">—</span>
                                )}
                            </td>
                            <td className="align-middle">
                                {b64 ? <button className="btn btn-secondary btn-sm" onClick={() => downloadFile(nome, b64)} type="button">Download</button> : urlDownload ? <a href={urlDownload} download className="btn btn-secondary btn-sm">Download</a> : <span className="text-muted">—</span>}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

export default ListaAnexosSolicitante;
